import { useEffect, useRef, useState } from "react";
import { KeyRound, Mail } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPassword, registerWithPassword, requestMagicLink, loading } = useAuth();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [legacyLinkSent, setLegacyLinkSent] = useState(false);
  const handledGoogleError = useRef(false);

  useEffect(() => {
    if (handledGoogleError.current) return;
    const error = new URLSearchParams(location.search).get("error");
    const message = error === "google_not_configured" ? t.auth.googleNotConfigured
      : error === "google_cancelled" ? t.auth.googleCancelled
        : error === "google_failed" ? t.auth.googleFailed
          : null;
    if (!message) return;
    handledGoogleError.current = true;
    toast.error(message);
    navigate("/login", { replace: true });
  }, [location.search, navigate, t.auth.googleCancelled, t.auth.googleFailed, t.auth.googleNotConfigured]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (creatingAccount && password !== confirmPassword) {
      toast.error(t.auth.passwordsDoNotMatch);
      return;
    }
    try {
      if (creatingAccount) await registerWithPassword(email, password);
      else await loginWithPassword(email, password);
      navigate("/account");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.common.error);
    }
  }

  async function sendLegacyLink() {
    try { await requestMagicLink(email); setLegacyLinkSent(true); toast.success(t.auth.magicLinkSent); }
    catch (error) { toast.error(error instanceof Error ? error.message : t.common.error); }
  }

  return (
    <div className="page-shell">
      <div className="container max-w-5xl">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="panel panel-muted p-8 md:p-10">
            <span className="eyebrow">{t.auth.loginWithEmail}</span>
            <h1 className="mb-8 text-[3rem] leading-[1.14]">{creatingAccount ? t.auth.createAccount : t.auth.login}</h1>
            <p style={{ color: "var(--color-text-muted)" }}>{creatingAccount ? t.auth.passwordRegisterDescription : t.auth.passwordLoginDescription}</p>
          </section>

          <form onSubmit={handleSubmit} className="panel form-grid p-8 md:p-10">
            <div><label className="form-label" htmlFor="email">{t.auth.email}</label><input id="email" className="input-shell" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></div>
            <div><label className="form-label" htmlFor="password">{t.auth.password}</label><input id="password" className="input-shell" type="password" autoComplete={creatingAccount ? "new-password" : "current-password"} minLength={5} maxLength={128} required value={password} onChange={(event) => setPassword(event.target.value)} /><p className="mt-2 text-xs" style={{ color: "var(--color-text-light)" }}>{t.auth.passwordHint}</p></div>
            {creatingAccount && <div><label className="form-label" htmlFor="password-confirm">{t.auth.confirmPassword}</label><input id="password-confirm" className="input-shell" type="password" autoComplete="new-password" minLength={5} maxLength={128} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></div>}

            <button type="submit" disabled={loading} className="btn-primary flex w-full items-center justify-center gap-2 disabled:opacity-50">
              <KeyRound size={17} />
              {loading ? t.common.loading : creatingAccount ? t.auth.createAccount : t.auth.login}
            </button>
            {!creatingAccount && <button type="button" disabled={loading} onClick={() => { setCreatingAccount(true); setPassword(""); setConfirmPassword(""); }} className="btn-secondary flex w-full items-center justify-center gap-2 disabled:opacity-50">
              <KeyRound size={17} />
              {t.auth.createPassword}
            </button>}
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.16em]" style={{ color: "var(--color-text-light)" }}><span className="h-px flex-1" style={{ background: "var(--color-border)" }} />{t.auth.orContinueWith}<span className="h-px flex-1" style={{ background: "var(--color-border)" }} /></div>
            <a href="/api/auth/google/start" className="btn-secondary flex w-full items-center justify-center gap-2"><span className="font-semibold" aria-hidden="true">G</span>{t.auth.continueWithGoogle}</a>
            <p className="text-center text-sm" style={{ color: "var(--color-text-muted)" }}>{creatingAccount ? t.auth.hasAccount : t.auth.noAccount} <button type="button" className="underline" onClick={() => setCreatingAccount(!creatingAccount)}>{creatingAccount ? t.auth.login : t.auth.createAccount}</button></p>
            {!creatingAccount && <div className="border-t pt-5 text-center" style={{ borderColor: "var(--color-border)" }}><p className="text-xs" style={{ color: "var(--color-text-light)" }}>{t.auth.legacyPasswordHelp}</p><button type="button" disabled={loading} onClick={() => { if (!email) { toast.error(t.auth.legacyPasswordEmailRequired); return; } void sendLegacyLink(); }} className="mt-2 inline-flex items-center gap-2 text-sm underline disabled:opacity-50"><Mail size={14} />{t.auth.legacyPasswordAction}</button>{legacyLinkSent && <p className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>{t.auth.checkInbox}</p>}</div>}
          </form>
        </div>
      </div>
    </div>
  );
}
